import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { useInitLikedProducts } from '@widgets/like-product';
import { ProductCardCarousel } from '@common/product-card';
import {
  BestProductCarouselComponentType,
  convertProductItemListToUxGoodsCardItemList,
  convertProductListToIdList,
  convertToWebUrlFromDeeplink,
  useCarouselMoreButtonSlideWidth,
  useEnvConfig,
} from '@domains/storehome-common';

import { useBestProductCarouselUBL } from '../log/useBestProductCarouselUBL';

interface Params {
  shop_id: string;
  category_id?: string;
  component: BestProductCarouselComponentType;
}

export const useBestProductCarouselService = ({ shop_id, component: { category_group_list, header } }: Params) => {
  const [selectedId, setSelectedId] = useState(() => category_group_list[0]?.category.id);
  const router = useRouter();
  const { is_zigzag } = useEnvConfig();
  const { clickMoreButtonUBL, clickCarouselMoreButtonUBL, clickTabUBL } = useBestProductCarouselUBL({ shop_id });

  const carouselMoreButtonWidth = useCarouselMoreButtonSlideWidth();
  const tabs = useMemo(() => {
    return category_group_list.map((item) => ({ name: item?.category.name ?? '', id: item?.category.id ?? '' })) ?? [];
  }, [category_group_list]);

  const { products, actionButton, columnCount } = useMemo(() => {
    const selectedItem = category_group_list.find((item) => item?.category.id === selectedId);
    return {
      products: selectedItem?.item_list ?? [],
      actionButton: selectedItem?.action_button,
      columnCount: (selectedItem?.item_column_count ?? 3.14) as ProductCardCarousel.RootProps['slidesPerView'],
    };
  }, [selectedId, category_group_list]);

  const productCardList = useMemo(
    () =>
      convertProductItemListToUxGoodsCardItemList({
        item_list: products,
        is_zigzag,
        show_ranking: true,
      }),
    [products, is_zigzag],
  );

  useInitLikedProducts({
    data: productCardList,
    convert: convertProductListToIdList,
  });

  const handleMoreButtonClick = () => {
    if (!header.button?.landing_url) {
      return;
    }
    const link = convertToWebUrlFromDeeplink(header.button?.landing_url);
    clickMoreButtonUBL(header.button?.ubl);
    router.push(link);
  };

  const handleCarouselMoreButtonClick = () => {
    if (!actionButton?.link_url) {
      return;
    }
    const link = convertToWebUrlFromDeeplink(actionButton.link_url);
    clickCarouselMoreButtonUBL(actionButton.ubl);
    router.push(link);
  };

  const handleTabChange = (id: string) => {
    const tab = category_group_list.find((item) => item?.category.id === id);
    clickTabUBL(tab?.category.ubl);
    setSelectedId(id);
  };

  return {
    tabs,
    columnCount,
    selectedId,
    carouselMoreButtonWidth,
    productCardList,
    handleCarouselMoreButtonClick,
    handleMoreButtonClick,
    actionButton,
    handleTabChange,
  };
};
